// src/reporting/renderer/blocks/TableBlock.js
import React, { useState, useMemo } from "react";
import { evalBinding } from "../../lib/bindings";
import { fmtMoney, fmtDate } from "../../lib/format";

const singularLabels: Record<string, string> = {
  entries: "entry",
};

const countLabel = (count: number, plural: string, explicitSingular?: string) =>
  count === 1 ? (explicitSingular || singularLabels[plural] || plural.replace(/s$/, "")) : plural;

export default function TableBlock({ block, data, onSortChange }: any) {
  const safeRows = useMemo(() => {
    const rows = evalBinding(block.rows, data);
    return Array.isArray(rows) ? rows : [];
  }, [block.rows, data]);

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = (key: string) => {
    let newKey: string | null;
    let newDir: 'asc' | 'desc';
    if (sortKey === key) {
      if (sortDir === 'asc') { newKey = key; newDir = 'desc'; }
      else { newKey = null; newDir = 'asc'; }
    } else {
      newKey = key; newDir = 'asc';
    }
    setSortKey(newKey);
    setSortDir(newDir);
    if (onSortChange) onSortChange({ key: newKey, dir: newDir, rowsPath: block.rows?.path });
  };

  const sortedRows = useMemo(() => {
    if (!sortKey) return safeRows;
    return [...safeRows].sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      const numA = Number(av);
      const numB = Number(bv);
      if (!isNaN(numA) && !isNaN(numB)) {
        return sortDir === 'asc' ? numA - numB : numB - numA;
      }
      const cmp = String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [safeRows, sortKey, sortDir]);

  const fmt = (v: any, kind: any) =>
    kind === "money" ? fmtMoney(v) :
    kind === "date"  ? fmtDate(v)  :
    kind === "status" ? (
      <span className={`status-badge status-${String(v || "").toLowerCase()}`}>
        {v ?? ""}
      </span>
    ) :
    (v ?? "");

  // label can be:
  // - string (literal header text)
  // - binding object (e.g. { expr: "..." } or { path: "..." })
  const renderLabel = (label: any) => {
    if (label == null) return "";
    if (typeof label === "string") return label;
    return evalBinding(label, data) ?? "";
  };

  const wrapCls = block.className || "table-wrap";
  const tableCls = block.tableClassName || "";
  const tableTitle = block.title ? String(evalBinding(block.title, data) ?? "Report table") : "Report table";
  const colStyle = (c: any): React.CSSProperties | undefined => {
    if (!c.width) return undefined;
    const width = typeof c.width === "number" ? `${c.width}px` : c.width;
    return { width };
  };
  const columns = block.columns || [];

  // Row-level class from a data field (e.g., rowClassField: "status" → "row-pending")
  const rowClassField = block.rowClassField;
  const getRowClass = (r: any) => {
    const base = block.rowClassName || "";
    if (!rowClassField || !r?.[rowClassField]) return base;
    const val = String(r[rowClassField]).toLowerCase();
    return `${base} row-${val}`.trim();
  };

  // Summary badges: pre-computed array or auto-computed from summaryConfig
  const summaryBadges: any[] = (() => {
    if (block.summaryBadges) {
      const badges = Array.isArray(block.summaryBadges)
        ? block.summaryBadges
        : evalBinding(block.summaryBadges, data);
      return Array.isArray(badges) ? badges : [];
    }
    if (!block.summaryConfig) return [];
    const { field, badges = [], totalLabel = "entries", singularTotalLabel } = block.summaryConfig;
    const counts: Record<string, number> = {};
    for (const r of safeRows) {
      const v = String(r?.[field] || "").toLowerCase();
      counts[v] = (counts[v] || 0) + 1;
    }
    const result: any[] = [
      { status: "total", count: safeRows.length, label: countLabel(safeRows.length, totalLabel, singularTotalLabel) },
    ];
    for (const b of badges) {
      if (counts[b.value]) {
        result.push({ status: b.value, count: counts[b.value], label: b.label });
      }
    }
    return result;
  })();

  return (
    <div className={wrapCls} role="region" aria-label={tableTitle} tabIndex={0}>
      {block.title && <div className="h3">{tableTitle}</div>}

      {summaryBadges.length > 0 && (
        <div className="table-summary-badges">
          {summaryBadges.map((b: any, i: number) => (
            <span key={i} className={`summary-badge summary-badge--${b.status}`}>
              {b.count} {b.label}
            </span>
          ))}
        </div>
      )}

      <table className={tableCls}>
        {columns.some((c: any) => c.width) && (
          <colgroup>
            {columns.map((c: any) => (
              <col key={c.key} style={colStyle(c)} />
            ))}
          </colgroup>
        )}
        <thead>
          <tr>
            {columns.map((c: any) => (
              <th
                key={c.key}
                className={c.thClassName || c.className || ""}
                style={{ ...colStyle(c), cursor: "pointer", userSelect: "none" }}
                onClick={() => handleSort(c.key)}
              >
                {renderLabel(c.label)}
                {sortKey === c.key && (
                  <span className="no-print" style={{ marginLeft: 4, fontSize: 10 }}>
                    {sortDir === 'asc' ? '▲' : '▼'}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {sortedRows.map((r: any, i: number) => (
            <tr key={i} className={getRowClass(r)}>
              {columns.map((c: any) => (
                <td
                  key={c.key}
                  className={c.tdClassName || c.className || ""}
                  style={colStyle(c)}
                >
                  {fmt(r?.[c.key], c.format)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>

        {block.footer && (
          <tfoot>
            <tr className={block.footerRowClassName || ""}>
              {(block.footer.cells || []).map((c: any, i: number) => (
                <td key={i}>{evalBinding(c, data)}</td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
